import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Search, BookOpen } from "lucide-react";

/** Search the tenant Diagnosis Library by code or title and pick one entry. */
export function DiagnosisPicker({ onPick, testId = "dx-picker", placeholder = "Search by ICD-10, ICD-9, SNOMED, or description" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/diagnosis-library/search", { params: { q, limit: 15 } })
        .then((r) => { setRows(r.data || []); setActive(0); })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const pick = (row) => { onPick(row); setQ(""); setOpen(false); };
  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && rows[active]) { e.preventDefault(); pick(rows[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input data-testid={testId} value={q} placeholder={placeholder} autoComplete="off"
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          className="h-11 w-full rounded-lg border border-[#C9B8A6] bg-[#FDFCF8] pl-9 pr-3 text-sm text-[#3E2723] placeholder:text-[#84675B] outline-none focus:ring-2 focus:ring-[#8D6E63]" />
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
      </div>
      {open && (
        <div data-testid={`${testId}-results`} className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
          {loading && rows.length === 0 ? (
            <div className="p-3 text-xs text-[#7B6157]">Searching the Diagnosis Library…</div>
          ) : rows.length === 0 ? (
            <div className="p-3 text-xs text-[#7B6157]" data-testid={`${testId}-empty`}>No active library match. An admin can add it in Settings, Diagnosis Library.</div>
          ) : (<>
            <div className="grid grid-cols-[64px_64px_92px_1fr] gap-2 border-b border-[#F1EEE7] bg-[#FDFCF8] px-3 py-1.5 text-[9px] font-bold uppercase tracking-wide text-[#84675B]"><span>ICD-10</span><span>ICD-9</span><span>SNOMED</span><span>Description</span></div>
            {rows.map((r, i) => (
            <button key={r.id} type="button" data-testid={`${testId}-option-${r.code}`} onClick={() => pick(r)} onMouseEnter={() => setActive(i)}
              className={`grid w-full grid-cols-[64px_64px_92px_1fr] items-start gap-2 px-3 py-2 text-left transition-colors ${i === active ? "bg-[#F5F2EB]" : "hover:bg-[#FDFCF8]"}`}>
              <span className="mt-0.5 font-mono text-[12px] font-bold text-[#3E2723]">{r.code}</span>
              <span className="mt-0.5 font-mono text-[11px] text-[#6D4C41]">{r.icd9 || "-"}</span>
              <span className="mt-0.5 truncate font-mono text-[11px] text-[#6D4C41]" title={r.snomed_title || undefined}>{r.snomed || "-"}</span>
              <span className="min-w-0">
                <span className="block text-sm text-[#4E342E]">{r.title}</span>
                {r.snomed_title && <span className="block text-[10px] text-[#7B6157]">SNOMED: {r.snomed_title}</span>}
                <span className="mt-0.5 flex items-center gap-1 text-[10px] text-[#84675B]"><BookOpen size={10} /> {r.category}{r.is_sample ? " · starter sample" : ""}</span>
              </span>
            </button>
          ))}</>)}
        </div>
      )}
    </div>
  );
}
